import { useOrg } from '../../context/OrgContext';
import { Building2, ChevronDown, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

function getOrgInitials(name: string) {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function OrgSwitcher() {
  const { organizations, activeOrg, setActiveOrg, isLoading } = useOrg();

  if (isLoading || !activeOrg) {
    return (
      <div className="h-9 w-32 bg-gray-100 rounded animate-pulse" />
    );
  }

  if (organizations.length <= 1) {
    // Single org - just show the name, no dropdown
    return (
      <div className="flex items-center gap-2 px-2 py-1">
        <Avatar className="h-6 w-6">
          <AvatarImage src={activeOrg.logo || undefined} />
          <AvatarFallback className="text-[10px] bg-blue-100 text-blue-700">
            {getOrgInitials(activeOrg.name)}
          </AvatarFallback>
        </Avatar>
        <span className="text-sm font-medium text-gray-700 max-w-[120px] truncate">
          {activeOrg.name}
        </span>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors outline-none">
        <Avatar className="h-6 w-6">
          <AvatarImage src={activeOrg.logo || undefined} />
          <AvatarFallback className="text-[10px] bg-blue-100 text-blue-700">
            {activeOrg ? getOrgInitials(activeOrg.name) : <Building2 className="h-3 w-3" />}
          </AvatarFallback>
        </Avatar>
        <span className="text-sm font-medium text-gray-700 max-w-[120px] truncate">
          {activeOrg.name}
        </span>
        <ChevronDown className="h-3 w-3 text-gray-400" />
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-56" align="start">
        <DropdownMenuLabel>Organizations</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {organizations.map((org) => (
          <DropdownMenuItem
            key={org.id}
            onClick={() => setActiveOrg(org.slug)}
            className="flex items-center gap-2"
          >
            <Avatar className="h-6 w-6">
              <AvatarImage src={org.logo || undefined} />
              <AvatarFallback className="text-[10px] bg-blue-50 text-blue-700">
                {getOrgInitials(org.name)}
              </AvatarFallback>
            </Avatar>
            <span className="flex-1 truncate">{org.name}</span>
            {activeOrg?.id === org.id && (
              <Check className="h-4 w-4 text-blue-600" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
